import FormField from 'components/FormField';
import { useTranslate } from '@tolgee/react';
import { FV } from './share';
import useSmartField from 'hooks/useSmartField';
import dayjs, { Dayjs } from 'lib/dayjs';
import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { DisabledProps } from 'views/Aide/Scenario/TaskForm/share';
import { DatePicker, RangePickerProps } from 'components/DatePicker';
import { useHelpContext } from '../HelpContext';
import { InlineHelp } from '../Help';

export function useGetPrepareTimeRangeLabel() {
  const { t } = useTranslate();

  return {
    t,
    getPrepareTimeRangeLabel: useCallback(
      (activityType: Knowledge.Activity.Type) => {
        return activityType === 'CLEARANCE_SALE_ACTIVITY'
          ? t('促销预热期')
          : activityType === 'LIVE_STREAMING_ACTIVITY'
          ? t('直播预热期')
          : t('act_field_warmup_time');
      },
      [t],
    ),
  };
}

function PrepareTimeRange({ disabled }: DisabledProps) {
  const { t, getPrepareTimeRangeLabel } = useGetPrepareTimeRangeLabel();

  const {
    values: { activityType, activityFrom },
    errors,
  } = useFormikContext<FV>();

  const label = getPrepareTimeRangeLabel(activityType);

  const {
    field: { value: from, onChange: onFromChange },
  } = useSmartField<FV['activityPrepareFrom']>('activityPrepareFrom');

  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);

  const {
    stepState: [, setHelpStep],
  } = useHelpContext();

  return (
    <FormField<FV['activityPrepareTo']>
      name="activityPrepareTo"
      label={label}
      help={
        errors.activityPrepareTo ? undefined : (
          <div style={{ marginBottom: 12, fontSize: 12 }}>{t('warmup_tip')}</div>
        )
      }
      validate={(to) =>
        to && activityFrom && dayjs(to).isSameOrAfter(dayjs(activityFrom))
          ? activityType === 'CLEARANCE_SALE_ACTIVITY'
            ? t('促销预热期不能晚于促销日期')
            : activityType === 'LIVE_STREAMING_ACTIVITY'
            ? t('直播预热期不能晚于直播日期')
            : t('activity_prepare_time_tips')
          : undefined
      }
    >
      {({ field: { value: to, onChange: onToChange, onBlur } }) => {
        const value: RangePickerProps['value'] = from && to ? [dayjs(from), dayjs(to)] : undefined;

        const picker = (
          <DatePicker.RangePicker
            disabled={disabled}
            showTime={{
              defaultValue: [
                dayjs('00:00:00', 'HH:mm:ss'),
                range[0] ? range[0].clone().subtract(1, 'second') : dayjs('00:00:00', 'HH:mm:ss'),
              ],
              showSecond: false,
            }}
            format="YYYY-MM-DD HH:mm"
            value={value}
            onCalendarChange={(range) => range && setRange(range)}
            onChange={(_, [from, to]) => {
              onFromChange(from || undefined);
              onToChange(to || undefined);
            }}
            onOpenChange={(open) => !open && setTimeout(onBlur)}
            onFocus={() => setHelpStep('活动预热期')}
          />
        );

        return <InlineHelp step="活动预热期">{picker}</InlineHelp>;
      }}
    </FormField>
  );
}

export default PrepareTimeRange;
